import { useState, useCallback, useMemo } from 'react';

/**
 * Reusable hook for bulk-selection patterns on admin tables.
 *
 * Usage:
 *   const sel = useBulkSelection(items, (item) => item.id);
 *   <input type="checkbox" checked={sel.isAllSelected} onChange={sel.toggleAll} />
 *   <input type="checkbox" checked={sel.isSelected(item.id)} onChange={() => sel.toggle(item.id)} />
 *   sel.selectedIds  // string[]
 *   sel.selectedCount
 *   sel.clear()
 *
 * The hook is pure: it doesn't touch the network. Pair it with `<BulkActionToolbar />`
 * for the UI and a `bulkDelete()` API call to perform actual mutations.
 */
export function useBulkSelection(items = [], getId = (i) => i?.id) {
  const [selected, setSelected] = useState(() => new Set());

  // Stable list of currently-visible ids (derived from items prop).
  const visibleIds = useMemo(
    () => items.map((it) => getId(it)).filter(Boolean),
    [items, getId]
  );

  const isSelected = useCallback((id) => selected.has(id), [selected]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(visibleIds));
  }, [visibleIds]);

  const clear = useCallback(() => {
    setSelected(new Set());
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === visibleIds.length && visibleIds.length > 0) return new Set();
      return new Set(visibleIds);
    });
  }, [visibleIds]);

  // Only count selections that are currently visible (handles paginated / filtered lists).
  const selectedIds = useMemo(
    () => visibleIds.filter((id) => selected.has(id)),
    [visibleIds, selected]
  );

  const isAllSelected = visibleIds.length > 0 && selectedIds.length === visibleIds.length;
  const isPartiallySelected = selectedIds.length > 0 && !isAllSelected;

  return {
    selectedIds,
    selectedCount: selectedIds.length,
    isSelected,
    isAllSelected,
    isPartiallySelected,
    toggle,
    toggleAll,
    selectAll,
    clear,
  };
}
